import { useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient as qc } from '@/lib/query';
import { keys as invoiceKeys, splitKeys, TSplitPart } from '@/models/invoices';
import { type LineRef } from '@/models/lineKey';
import { invalidateNavCounters } from '@/models/nav-counters';
import { offerKeys } from '@/models/offers';

export type { LineRef } from '@/models/lineKey';

export type SplitPlanStatus =
  | 'draft'
  | 'proposed'
  | 'returned'
  | 'approved'
  | 'committed'
  | 'cancelled';

export type TSplitLine = LineRef & {
  serviceName: string;
  priceAmount: number;
  priceCurrency: string;
  blockTitle: string | null;
  cpName: string | null;
};

export type TSplitPlanAltItem = {
  seq: number;
  currency: string;
  paymentMethod: string;
  payerId: number | null;
  amount: number | null;
  lines: LineRef[];
};

export type TSplitPlan = {
  id: number;
  offerId: number;
  status: SplitPlanStatus;
  proposedBy: number | null;
  reviewedBy: number | null;
  reviewNote: string | null;
  srNote: string | null;
  reviewAltItems: TSplitPlanAltItem[] | null;
  committedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TCoveredLine = LineRef & {
  coverageId: number;
  deliveredAt: string | null;
};

export type TSplitPlanItem = {
  id: number;
  seq: number;
  currency: string;
  paymentMethod: string;
  payerId: number | null;
  invoiceId: number | null;
  /** Independent billed amount (may arrive as a numeric string from pg). */
  amount: number | string | null;
  coveredAt: string | null;
  /** BizDev's description of this installment, shown to the customer. */
  note: string | null;
  fulfilled: boolean;
  invoicePaid: boolean;
  coveredLines: TCoveredLine[];
  /**
   * Set by a currency change while the installment was not issued: the rates into its
   * currency it will be billed at, and when they were taken.
   */
  frozenRates?: Record<string, number> | null;
  frozenRatesAt?: string | null;
};

export type TSplitPlanResponse = {
  /**
   * What the schedule must add up to, valued by the server in the plan's base currency.
   * Null while the offer has no confirmed selection to value.
   */
  orderNet: {
    /** The order before discount. Shown for context, never the thing reconciled. */
    netMinor: number;
    /** What the customer agreed to pay, discount applied. Installments must total this. */
    agreedMinor: number;
    discountPercent: number;
    baseCurrency: string;
  } | null;
  offerId: number;
  accountId: number | null;
  splitEnabled: boolean;
  plan: TSplitPlan | null;
  altFingerprint?: string | null;
  items: TSplitPlanItem[];
  issuableSeq: number | null;
  /** Why the issuable installment cannot be issued yet, e.g. no Monday opportunity. */
  issueBlockReason?: string | null;
  selectedLines: TSplitLine[];
  parts: TSplitPart[];
  offerStatus: {
    mode: string;
    badge: string | null;
    totalServices: number;
    paidItems: number;
    currencyTotals: Record<
      string,
      { paid: number; outstanding: number }
    > | null;
  } | null;
};

export type TSplitPlanItemInput = {
  seq: number;
  currency: string;
  paymentMethod: string;
  payerId?: number | null;
  amount: number | null;
  lines: LineRef[];
};

export type TReviewDecision = 'approve' | 'return';

export type TReviewQueueRow = {
  id: number;
  offerId: number;
  status: 'proposed' | 'returned' | 'approved';
  srNote: string | null;
  createdAt: string;
  updatedAt: string;
  offerTitle: string;
  contactName: string;
  contactEmail: string;
  accountName: string;
  proposedByEmail: string;
  proposedByName: string;
  installments: number;
  totalAmount: string | null;
  currency: string;
  assignedLines: number;
  selectionConfirmed: boolean;
  selectedLines: number;
};

export const splitPlanKeys = {
  plan: (offerId: number) => ['split-plan', offerId] as const,
  reviewQueue: () => ['split-plan', 'review-queue'] as const,
};

const callJson = <T>(
  endpoint: string,
  json?: any,
  method?: string,
): Promise<T> =>
  call<T>(endpoint, method ? { method, json } : { json }).then(
    (r) => r.json() as Promise<T>,
  );

const invalidatePlan = (offerId: number) => {
  qc.invalidateQueries({ queryKey: splitPlanKeys.plan(offerId) });
  qc.invalidateQueries({ queryKey: splitKeys.state(offerId) });
  qc.invalidateQueries({ queryKey: splitPlanKeys.reviewQueue() });
  invalidateNavCounters();
};

const invalidatePlanAndInvoices = (offerId: number) => {
  invalidatePlan(offerId);
  qc.invalidateQueries({ queryKey: invoiceKeys.all });
  qc.invalidateQueries({ queryKey: ['invoices', 'details-by-offer', offerId] });
};

export function useSplitPlan(offerId: number) {
  return useQuery(
    {
      queryKey: splitPlanKeys.plan(offerId),
      queryFn: () =>
        call('offer/split-plan', {
          method: 'GET',
          json: { offerId } as any,
        }).then((r) => r.json() as Promise<TSplitPlanResponse>),
      enabled: !!offerId,
    },
    qc,
  );
}

export function useReviewQueue() {
  return useQuery(
    {
      queryKey: splitPlanKeys.reviewQueue(),
      queryFn: () =>
        call('offer/split-plan/review-queue', { method: 'GET' }).then(
          (r) => r.json() as Promise<{ plans: TReviewQueueRow[] }>,
        ),
    },
    qc,
  );
}

export function useSaveSplitPlan(offerId: number) {
  return useMutation(
    {
      mutationFn: (items: TSplitPlanItemInput[]) =>
        callJson<{ plan: TSplitPlan; items: TSplitPlanItem[] }>(
          'offer/split-plan',
          {
            offerId,
            items,
          },
        ),
      onSuccess: () => invalidatePlan(offerId),
    },
    qc,
  );
}

export function useIssueInstallment(offerId: number) {
  return useMutation(
    {
      mutationFn: (planItemId: number) =>
        callJson<{ ok: boolean; invoiceId: number; issued: boolean }>(
          'offer/split-plan/issue-item',
          { offerId, planItemId },
        ),
      onSuccess: () => invalidatePlanAndInvoices(offerId),
    },
    qc,
  );
}

export function useProposeSplit(offerId: number) {
  return useMutation(
    {
      mutationFn: (note?: string) =>
        callJson<TSplitPlan>('offer/split-plan/propose', {
          offerId,
          note: note || null,
        }),
      onSuccess: () => invalidatePlan(offerId),
    },
    qc,
  );
}

export function useAssignLines(offerId: number) {
  return useMutation(
    {
      mutationFn: (
        assignments: {
          planItemId: number;
          lines: LineRef[];
          /** Omit to leave the stored note untouched; '' clears it. */
          note?: string | null;
        }[],
      ) =>
        callJson<{ items: TSplitPlanItem[] }>('offer/split-plan/assign-lines', {
          offerId,
          assignments,
        }),
      onSuccess: () => invalidatePlan(offerId),
    },
    qc,
  );
}

export function useWithdrawSplit(offerId: number) {
  return useMutation(
    {
      mutationFn: () =>
        callJson<TSplitPlan>('offer/split-plan/withdraw', { offerId }),
      onSuccess: () => invalidatePlan(offerId),
    },
    qc,
  );
}

export function useReviewSplit(offerId: number) {
  return useMutation(
    {
      mutationFn: (body: {
        decision: TReviewDecision;
        note?: string;
        altItems?: TSplitPlanItemInput[];
      }) =>
        callJson<TSplitPlan>('offer/split-plan/review', { offerId, ...body }),
      onSuccess: () => invalidatePlan(offerId),
    },
    qc,
  );
}

export function useCancelSplit(offerId: number) {
  return useMutation(
    {
      mutationFn: () =>
        callJson<TSplitPlan>('offer/split-plan/cancel', { offerId }),
      onSuccess: () => invalidatePlanAndInvoices(offerId),
    },
    qc,
  );
}

export function useAdoptAltSplit(offerId: number) {
  return useMutation(
    {
      // The fingerprint identifies the alternative the SR was actually shown. If
      // BizDev replaced it meanwhile, the API refuses rather than publishing numbers
      // nobody reviewed.
      mutationFn: (altFingerprint?: string | null) =>
        callJson<{ plan: TSplitPlan; items: TSplitPlanItem[] }>(
          'offer/split-plan/adopt-alt',
          {
            offerId,
            ...(altFingerprint ? { altFingerprint } : {}),
          },
        ),
      onSuccess: () => invalidatePlan(offerId),
    },
    qc,
  );
}

export function useSetSplitEnabled(offerId: number) {
  return useMutation(
    {
      mutationFn: (enabled: boolean) =>
        callJson<{ id: number; uuid: string; splitEnabled: boolean }>(
          'offer/set-split-enabled',
          {
            offerId,
            enabled,
          },
        ),
      onSuccess: () => {
        invalidatePlan(offerId);
        qc.invalidateQueries({ queryKey: offerKeys.detail(offerId) });
      },
    },
    qc,
  );
}
